import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Select, message } from "antd";
import AdminJewelryReportService from "../../../services/admin-jewelryReport-service";
import { BlobToDownloadFile } from "../../../helper/commanFunction";
import ReportScaffold from "../../ChartsGroup/DiamondReportsCharts/_shared/ReportScaffold";
import { exportChartPdf } from "../../ChartsGroup/DiamondReportsCharts/_shared/exportChartPdf";
import { ADMIN_VENDOR_CLICKS_CONFIG } from "./_shared/adminJewelryReportConfigs";
import JewelryDetailSearchTable from "../../reports/jewelry/_shared/JewelryDetailSearchTable";
import { extractDealerIds, fetchPriorPeriodTotalClicks } from "../../reports/jewelry/_shared/jewelryHelpers";

const { Option } = Select;

// Ported bug fix: the pre-redesign page's table-filter click-drill sent the
// clicked vendor's company name into the `retailer` request field instead of
// `vendor` (the table SP self-resolves that field as a retailer company
// name, so the filter silently matched nothing). Fixed here by wiring the
// click straight to JewelryDetailSearchTable's filterParamName="vendor".
const JewelryVendorClicksReport = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);

  const [dealerId, setDealerId] = useState();
  const [forDealer, setForDealer] = useState("");
  const [dataRangeFrom, setDataRangeFrom] = useState(moment().subtract(30, "days").format("YYYY-MM-DD"));
  const [dataRangeTo, setDataRangeTo] = useState(moment().format("YYYY-MM-DD"));
  const [vendorIds, setVendorIds] = useState([]);
  const [retailerIds, setRetailerIds] = useState([]);
  const [vendorList, setVendorList] = useState([]);
  const [retailerList, setRetailerList] = useState([]);
  const [vendorClicksData, setVendorClicksData] = useState([]);
  const [totalClicks, setTotalClicks] = useState(0);
  const [loading, setLoading] = useState(false);
  const [refetchToken, setRefetchToken] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [activityPdfLoading, setActivityPdfLoading] = useState(false);
  const [chartType, setChartType] = useState("bar");
  const [priorTotalClicks, setPriorTotalClicks] = useState(null);

  useEffect(() => {
    if (loginDetials?.loginDetials?.responseData?.logInSucceeded) {
      const id = loginDetials.loginDetials.responseData.dealerId;
      setDealerId(id);
      handleGetForDealer(id);
    }
    handleGetVendorDropdownData();
    handleGetRetailerDropdownData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGetForDealer = (id) => {
    AdminJewelryReportService.GetForDealer({ dealerID: id }).then((response) => {
      if (response.data.message === "Success") {
        const fd = response.data.responseData.forDealer;
        setForDealer(fd);
        handleLoadVendorClicksData(id, fd, dataRangeFrom, dataRangeTo, [], []);
      }
    });
  };

  const handleGetVendorDropdownData = () => {
    const id = loginDetials?.loginDetials?.responseData?.dealerId;
    AdminJewelryReportService.GetVendorDropdownList({ dealerId: id, reporttype: "Global" }).then((response) => {
      if (response.data.message === "Success") setVendorList(response.data.responseData);
    });
  };

  const handleGetRetailerDropdownData = () => {
    AdminJewelryReportService.GetRetailerDropdownList({ dealerId: 0, reporttype: "Global" }).then((response) => {
      if (response.data.message === "Success") setRetailerList(response.data.responseData);
    });
  };

  const handleVendorChange = (values) => {
    setVendorIds(values);
    if (values.length === 0) {
      handleGetRetailerDropdownData();
    } else {
      AdminJewelryReportService.ChangeRetailerList({ vendorIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setRetailerList(response.data.responseData);
      });
    }
  };

  const handleRetailerChange = (values) => {
    setRetailerIds(values);
    if (values.length === 0) {
      handleGetVendorDropdownData();
    } else {
      AdminJewelryReportService.ChangeVendorList({ retailerIds: extractDealerIds(values).toString() }).then((response) => {
        if (response.data.message === "Success") setVendorList(response.data.responseData);
      });
    }
  };

  const handleLoadVendorClicksData = (dealerIdArg, forDealerArg, fromDate, toDate, vIds, rIds) => {
    setLoading(true);
    AdminJewelryReportService.GetClicksBasedOnVendor({
      dealerID: dealerIdArg,
      forDealer: forDealerArg,
      fromDate: moment(fromDate).format("YYYY-MM-DD"),
      toDate: moment(toDate).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(rIds).toString(),
      vendorIds: extractDealerIds(vIds).toString(),
      reporttype: "Global",
    })
      .then((response) => {
        setLoading(false);
        if (response.data.message === "Success" && response.data.responseData) {
          const rows = response.data.responseData;
          setVendorClicksData(rows);
          setTotalClicks(rows.reduce((sum, row) => sum + Number(row.total || 0), 0));
        } else {
          setVendorClicksData([]);
          setTotalClicks(0);
        }
      })
      .catch(() => {
        setLoading(false);
        setVendorClicksData([]);
        setTotalClicks(0);
      });

    setPriorTotalClicks(null);
    fetchPriorPeriodTotalClicks(
      AdminJewelryReportService.GetClicksBasedOnVendor,
      (pFrom, pTo) => ({
        dealerID: dealerIdArg,
        forDealer: forDealerArg,
        fromDate: pFrom,
        toDate: pTo,
        retailerIds: extractDealerIds(rIds).toString(),
        vendorIds: extractDealerIds(vIds).toString(),
        reporttype: "Global",
      }),
      fromDate,
      toDate,
      "total",
      (data) => data
    ).then(setPriorTotalClicks);
  };

  const onDateChange = (dates, dateStrings) => {
    if (dates) {
      setDataRangeFrom(dateStrings[0]);
      setDataRangeTo(dateStrings[1]);
    }
  };

  const handleApplyFilter = () => {
    setFilterValue("");
    handleLoadVendorClicksData(dealerId, forDealer, dataRangeFrom, dataRangeTo, vendorIds, retailerIds);
    setRefetchToken((tok) => tok + 1);
  };

  const handleSliceClick = (vendorName) => setFilterValue(vendorName);

  const handleDownloadChart = async () => {
    setPdfLoading(true);
    const vendorNames = vendorIds.length === 0 ? t("All Vendors") : vendorList.filter((v) => extractDealerIds(vendorIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    const retailerNames = retailerIds.length === 0 ? t("All Retailers") : retailerList.filter((v) => extractDealerIds(retailerIds).includes(v.dealerID.toString())).map((v) => v.dealerCompany).join(", ");
    try {
      await exportChartPdf({
        elementId: "pdf-content",
        title: t(ADMIN_VENDOR_CLICKS_CONFIG.pageTitle),
        dealerCompany: loginDetials?.loginDetials?.responseData?.dealerCompany,
        dealerId,
        dataRangeFrom,
        dataRangeTo,
        infoLines: [`Vendors: ${vendorNames}`, `Retailers: ${retailerNames}`, `Total Clicks: ${totalClicks}`],
        fileName: "Admin-Jewelry-Vendor-Clicks-Report.pdf",
      });
    } finally {
      setPdfLoading(false);
    }
  };

  const sortedVendors = [...vendorClicksData].sort((a, b) => Number(b.total || 0) - Number(a.total || 0));
  const topVendor = sortedVendors[0];
  const top3Sum = sortedVendors.slice(0, 3).reduce((sum, row) => sum + Number(row.total || 0), 0);
  const top3Pct = totalClicks > 0 ? Math.round((top3Sum / totalClicks) * 100) : 0;
  const topPct = totalClicks > 0 && topVendor ? ((Number(topVendor.total) / totalClicks) * 100).toFixed(2) : 0;

  // This page never had a real Download PDF button - only the
  // client-side exportChartPdf() screenshot ("Download Chart", now
  // hidden platform-wide). Admin_JewelryReportController.ExportMetricPdf
  // now has a VendorClick/RetailerClick case added specifically for
  // this page (Task: Admin Reports missing Download PDF entirely,
  // 2026-09-09).
  const handleDownloadActivityPdf = () => {
    let inputData = {
      dealerID: dealerId,
      forDealer: forDealer,
      fromDate: moment(dataRangeFrom).format("YYYY-MM-DD"),
      toDate: moment(dataRangeTo).format("YYYY-MM-DD"),
      retailerIds: extractDealerIds(retailerIds).toString(),
      vendorIds: extractDealerIds(vendorIds).toString(),
      reportName: "VendorClick",
      barViewDimensions: chartType === "bar" ? "VendorClick" : "",
    };
    setActivityPdfLoading(true);
    AdminJewelryReportService.ExportMetricPdf(inputData)
      .then((response) => {
        setActivityPdfLoading(false);
        const filename = `AdminJewelry_VendorClick_${dataRangeFrom}_${dataRangeTo}.pdf`;
        BlobToDownloadFile(response.data, filename, "pdf");
      })
      .catch(() => {
        setActivityPdfLoading(false);
        message.error(t("Failed to generate PDF. Please try again."));
      });
  };

  return (
    <ReportScaffold
      config={ADMIN_VENDOR_CLICKS_CONFIG}
      report={{
        [ADMIN_VENDOR_CLICKS_CONFIG.dataKey]: vendorClicksData,
        JewelryTotalClicks: [{ TotalClickCnt: totalClicks }],
        JewelrySummaryKPIs: priorTotalClicks != null ? [{ Metric: "TotalClicks", CurrentValue: totalClicks, PriorValue: priorTotalClicks }] : [],
      }}
      loading={loading}
      dealerId={dealerId}
      forDealer={forDealer}
      dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
      globalDiamonds={{ dataRangeFrom, dataRangeTo }}
      onDateChange={onDateChange}
      onApplyFilter={handleApplyFilter}
      refetchToken={refetchToken}
      onDownloadChart={handleDownloadChart}
      downloadingChart={pdfLoading}
      onDownloadActivityPdf={handleDownloadActivityPdf}
      downloadingActivityPdf={activityPdfLoading}
      onChartClick={handleSliceClick}
      onChartTypeChange={setChartType}
      insight={
        topVendor && (
          <div className="edr-insight">
            💡 <b>{topVendor.dealerCompany}</b> {t("is the standout vendor at")} <b>{topPct}%</b> {t("of clicks")}, {t("and the top 3 vendors capture")}{" "}
            <b>{top3Pct}%</b> {t("of engagement")} —{" "}
            {top3Pct > 70
              ? t("brand engagement is highly concentrated in a few vendors.")
              : t("engagement is reasonably spread across vendors.")}
          </div>
        )
      }
      extraFilterFields={
        <>
          <div className="col-md-3">
            <div className="input__block">
              <label>{t("Vendor(s)")}</label>
              <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Vendors")} optionFilterProp="children" value={vendorIds} onChange={handleVendorChange}>
                {vendorList.map((item) => (
                  <Option key={`vendor-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                ))}
              </Select>
            </div>
          </div>
          <div className="col-md-3">
            <div className="input__block">
              <label>{t("Retailer(s)")}</label>
              <Select className="border__grey" showSearch showArrow mode="multiple" placeholder={t("Select One or More Retailers")} optionFilterProp="children" value={retailerIds} onChange={handleRetailerChange}>
                {retailerList.map((item) => (
                  <Option key={`retailer-${item.dealerID}`} value={`${item.dealerID}|${item.dealerCompany}`}>{item.dealerCompany}</Option>
                ))}
              </Select>
            </div>
          </div>
        </>
      }
      detailTable={
        <JewelryDetailSearchTable
          dealerId={dealerId}
          forDealer={forDealer}
          dataRangeFrom={dataRangeFrom}
          dataRangeTo={dataRangeTo}
          vendorIds={vendorIds}
          retailerIds={retailerIds}
          dealerMembershipType="Admin"
          filterParamName={ADMIN_VENDOR_CLICKS_CONFIG.detail.filterParamName}
          filterValue={filterValue}
          fileNamePart={ADMIN_VENDOR_CLICKS_CONFIG.detail.fileNamePart}
          dealerCompany={loginDetials?.loginDetials?.responseData?.dealerCompany}
          refetchToken={refetchToken}
          reportType="Global"
          title={t("Product Click Log")}
        />
      }
    />
  );
};

export default JewelryVendorClicksReport;
